'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useUnit } from 'effector-react';
import { $aiSessions, $aiChat, deleteMessageEvent } from '@nx-frontend/shared/store/transcript-panel';
import { $user } from '@nx-frontend/shared/store/user';
import { TranscriptAiCard } from './transcript-ai-card';

export function TranscriptAiSession() {
  const [aiSessions, aiChat, user, deleteMessage] = useUnit([$aiSessions, $aiChat, $user, deleteMessageEvent]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const userShortName = useMemo(() => {
    let userLetter = '';
    if (user?.lastname && user?.firstname) {
      userLetter = user?.firstname.slice(0, 1).toUpperCase() + user?.lastname.slice(0, 1).toUpperCase();
    } else if (user?.username) {
      userLetter = user?.username.slice(0, 2).toUpperCase();
    }
    return userLetter;
  }, [user]);

  const preparedCards = useMemo(() => {
    if (aiSessions.length) {
      const session = aiSessions[0];
      const cards: {
        index: number;
        role: string;
        content: string;
        inProgress: boolean;
        last: boolean;
        icon: {
          color: string;
          text?: number;
        };
        userShortName: string;
      }[] = [];
      for (let i = 0; i < session.messages.length; i++) {
        cards.push({
          index: i,
          role: session.messages[i].role,
          content: session.messages[i].content,
          inProgress: i === session.messages.length - 1 && 'progress' === aiChat.state ? true : false,
          last: i === session.messages.length - 1 ? true : false,
          icon: {
            color: 'sky',
            text: 400,
          },
          userShortName: userShortName,
        });
      }
      return cards;
    }
    return [];
  }, [aiChat.state, aiSessions, userShortName]);

  useEffect(() => {
    if (aiChat.state === 'progress') {
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [preparedCards, aiChat.state]);

  const handleRemoveCard = useCallback(
    (index: number) => {
      if (aiSessions.length) {
        const session = aiSessions[0];
        deleteMessage({
          globalId: session.globalId,
          messageId: index,
        });
      }
    },
    [aiSessions, deleteMessage]
  );

  return (
    <div className="flex flex-col rounded-[8px] border border-[#E5E5E5] h-full bg-white">
      <div className="bg-purple-50 text-purple-400"></div>
      <div className="rounded-t-[8px] bg-cyan-900 h-[42px] flex items-center justify-end px-[15px]">
        <div className="flex"></div>
      </div>
      <div className="grow flex flex-col pl-[16px] pr-[8px] py-[8px]">
        <div ref={scrollContainerRef} className="grow overflow-y-auto h-1 pr-[8px]">
          {preparedCards.map((card, index) => (
            <TranscriptAiCard key={index} {...card} onRemove={() => handleRemoveCard(index)} />
          ))}
        </div>
      </div>
    </div>
  );
}
